import { createSlice } from '@reduxjs/toolkit';
import { createAsyncThunkWithLoggingAndRetry } from '../../../store/middleware/thunk_helper';
import {
  cancelAppointment as cancelAppointmentService,
  fetchAppointmentByCancelToken as fetchAppointmentByCancelTokenService,
  fetchCanceledAppointments as fetchCanceledAppointmentsService,
  submitEmployerPHARequest,
  getAppointmentsSchedule as getAppointmentsScheduleService,
  saveAppointmentsSchedule as saveAppointmentsScheduleService,
  getAppointmentSlotAvailability as getAppointmentSlotAvailabilityService,
  getAppointmentSlotOverrides as getAppointmentSlotOverridesService,
  saveAppointmentSlotOverrides as saveAppointmentSlotOverridesService,
  deleteAppointmentSlotOverride as deleteAppointmentSlotOverrideService,
  getAppointmentRequests as getAppointmentRequestsService,
  exportAppointmentRequests as exportAppointmentRequestsService,
  sendAppointmentRequestSms as sendAppointmentRequestSmsService,
} from '../../../services/schedulerService';
import {
  getCMRAppointmentServices as getCMRAppointmentServicesService,
  updateCMRAppointmentServices as updateCMRAppointmentServicesService,
} from '../../../services/cmrRevenueCycleService';
import {
  getAdminSchedulerEventTypesPermissions,
  getBaseSetupSchedulerEventTypesPermissions,
} from '../../../selectors/scheduler';
import { PENDING, REQUESTING, SUCCESS, FAILED } from '../../../actions/statusTypes';
import {
  EVENT_TYPE_CMR,
  EVENT_TYPE_CMR_FLY,
  EVENT_TYPE_READINESS,
  EVENT_TYPES_REQUIRING_APPROVAL,
} from '../../../constants/eventTypes';
import { HOME_TAB, SETUP_TAB } from '../../../constants/tabs';

const _getAppointmentRequests = createAsyncThunkWithLoggingAndRetry(
  'scheduler/getAppointmentRequests',
  ({ employerId, eventType }, thunkAPI) => {
    const eventTypes = [EVENT_TYPE_CMR, EVENT_TYPE_CMR_FLY].includes(eventType)
      ? [EVENT_TYPE_CMR, EVENT_TYPE_CMR_FLY]
      : [eventType];

    return getAppointmentRequestsService(employerId, eventTypes);
  }
);
export function getAppointmentRequests({ employerId }) {
  return function (dispatch, getState) {
    const eventType = getState().screens.scheduler.selectedEventType;
    if (!eventType) return Promise.resolve(null);
    if (!EVENT_TYPES_REQUIRING_APPROVAL.includes(eventType)) return Promise.resolve(null);

    if (
      eventType === getState().screens.scheduler.aptsRequestActiveRequest?.eventType &&
      getState().screens.scheduler.appointmentRequestsFetchStatus === REQUESTING
    )
      return Promise.resolve(null);

    return dispatch(_getAppointmentRequests({ employerId, eventType }));
  };
}

export const deleteAppointmentSlotOverride = createAsyncThunkWithLoggingAndRetry(
  'scheduler/deleteAppointmentSlotOverride',
  ({ employerId, overrideId }, thunkAPI) => {
    return deleteAppointmentSlotOverrideService(employerId, overrideId);
  }
);

export const saveAppointmentSlotOverrides = createAsyncThunkWithLoggingAndRetry(
  'scheduler/saveAppointmentSlotOverrides',
  ({ employerId, overrides }, thunkAPI) => {
    return saveAppointmentSlotOverridesService(employerId, overrides);
  }
);

const getAppointmentSlotOverrides = createAsyncThunkWithLoggingAndRetry(
  'scheduler/getAppointmentSlotOverrides',
  ({ employerId, month, eventType }, thunkAPI) => {
    return getAppointmentSlotOverridesService(employerId, month, eventType);
  }
);

export const saveAppointmentsSchedule = createAsyncThunkWithLoggingAndRetry(
  'scheduler/saveAppointmentsSchedule',
  ({ employerId, schedule }, thunkAPI) => {
    return saveAppointmentsScheduleService(employerId, schedule);
  }
);

export const getAppointmentSlotAvailability = createAsyncThunkWithLoggingAndRetry(
  'scheduler/getAppointmentSlotAvailability',
  ({ employerId, eventType, month, employerKey }, thunkAPI) => {
    return getAppointmentSlotAvailabilityService({ employerId, eventType, month, employerKey });
  }
);

export function getAppointmentSlotAvailabilityAuthed({ employerId, month }) {
  return function (dispatch, getState) {
    const eventType = getState().screens.scheduler.selectedEventType;
    if (!eventType) return Promise.resolve(null);
    if (eventType === EVENT_TYPE_READINESS) return Promise.resolve(null);

    if (month) {
      dispatch(schedulerSlice.actions._setLastMonthFetched(month));
    } else {
      month = getState().screens.scheduler.lastMonthFetched;
    }

    const activeTab = getState().application.currentTabIndex;
    dispatch(
      getAppointmentsSchedule({
        employerId,
        eventType,
        forSchedulerConsole: activeTab === HOME_TAB,
      })
    );
    if (activeTab === SETUP_TAB) return Promise.resolve(null);

    if (!month) return Promise.resolve(null);

    const aptsSlotOverrideActiveRequest =
      getState().screens.scheduler.aptsSlotOverrideActiveRequest;
    if (
      aptsSlotOverrideActiveRequest?.eventType === eventType &&
      aptsSlotOverrideActiveRequest?.month === month
    ) {
      return Promise.resolve(null);
    }
    dispatch(getAppointmentSlotOverrides({ employerId, eventType, month }));

    const aptsSlotAvailabilityActiveRequest =
      getState().screens.scheduler.aptsSlotAvailabilityActiveRequest;
    if (
      aptsSlotAvailabilityActiveRequest?.eventType === eventType &&
      aptsSlotAvailabilityActiveRequest?.month === month
    ) {
      return Promise.resolve(null);
    }
    return dispatch(getAppointmentSlotAvailability({ employerId, eventType, month }));
  };
}

const getAppointmentsScheduleAuthed = createAsyncThunkWithLoggingAndRetry(
  'scheduler/getAppointmentsScheduleAuthed',
  ({ employerId, eventType }, thunkAPI) => {
    return getAppointmentsScheduleService(employerId, eventType);
  }
);
export function getAppointmentsSchedule({ employerId, eventType, forSchedulerConsole = false }) {
  return function (dispatch, getState) {
    if (!eventType) return Promise.resolve(null);

    if (
      getState().screens.scheduler.scheduleDataActiveRequest?.eventType === eventType &&
      getState().screens.scheduler.scheduleDataStatus === REQUESTING
    )
      return Promise.resolve(null);

    if (!forSchedulerConsole) {
      const setupPerms = getBaseSetupSchedulerEventTypesPermissions(getState());
      if (!setupPerms[eventType]?.canView) return Promise.resolve(null);
      dispatch(schedulerSlice.actions._setSelectedEventType(eventType));
    }

    return dispatch(getAppointmentsScheduleAuthed({ employerId, eventType }));
  };
}

export const submitPHARequest = createAsyncThunkWithLoggingAndRetry(
  'scheduler/submitPHARequest',
  ({ requestObj, employerId }, thunkAPI) => {
    return submitEmployerPHARequest(requestObj, employerId);
  }
);

export const exportAppointmentRequests = createAsyncThunkWithLoggingAndRetry(
  'scheduler/exportAppointmentRequests',
  ({ employerId, exportData }, thunkAPI) => {
    return exportAppointmentRequestsService(employerId, exportData);
  }
);

export const sendAppointmentRequestSms = createAsyncThunkWithLoggingAndRetry(
  'scheduler/sendAppointmentRequestSms',
  ({ employerId, identifiers, message }, thunkAPI) => {
    return sendAppointmentRequestSmsService(employerId, identifiers, message);
  },
  { retry: false }
);

export const getCMRAppointmentServices = createAsyncThunkWithLoggingAndRetry(
  'scheduler/getCMRAppointmentServices',
  ({ employerId, eventId }) => {
    return getCMRAppointmentServicesService(employerId, eventId);
  }
);

export const updateCMRAppointmentServices = createAsyncThunkWithLoggingAndRetry(
  'scheduler/updateCMRAppointmentServices',
  ({ employerId, eventId, serviceIds }) => {
    return updateCMRAppointmentServicesService(employerId, eventId, serviceIds);
  }
);

export const fetchAppointmentByCancelToken = createAsyncThunkWithLoggingAndRetry(
  'scheduler/fetchAppointmentByCancelToken',
  ({ token }, thunkAPI) => {
    return fetchAppointmentByCancelTokenService(token);
  }
);
export const cancelAppointment = createAsyncThunkWithLoggingAndRetry(
  'scheduler/cancelAppointment',
  async ({ token, captchaToken }, thunkAPI) => {
    return await cancelAppointmentService(token, captchaToken);
  }
);

const _fetchCanceledAppointments = createAsyncThunkWithLoggingAndRetry(
  'scheduler/fetchCanceledAppointments',
  ({ employerId, eventType }, thunkAPI) => {
    const eventTypes = [EVENT_TYPE_CMR, EVENT_TYPE_CMR_FLY].includes(eventType)
      ? [EVENT_TYPE_CMR, EVENT_TYPE_CMR_FLY]
      : [eventType];
    return fetchCanceledAppointmentsService(employerId, eventTypes);
  },
  { retry: true }
);
export function fetchCanceledAppointments({ employerId }) {
  return function (dispatch, getState) {
    const eventType = getState().screens.scheduler.selectedEventType;
    if (!eventType) return Promise.resolve(null);
    if (
      eventType === getState().screens.scheduler.cancelledAptsRequestActiveRequest?.eventType &&
      getState().screens.scheduler.cancelledAptsRequestStatus === REQUESTING
    )
      return Promise.resolve(null);
    return dispatch(_fetchCanceledAppointments({ employerId, eventType }));
  };
}

export function setSelectedEventType(eventType) {
  return function (dispatch, getState) {
    if (eventType === getState().screens.scheduler.selectedEventType) return;
    dispatch(schedulerSlice.actions.resetEventTypeSpecificState());
    const permissions = getAdminSchedulerEventTypesPermissions(getState());
    if (!permissions[eventType]?.canView) {
      dispatch(schedulerSlice.actions._setNoPermissionForSelectedEventType(true));
    } else {
      dispatch(schedulerSlice.actions._setSelectedEventType(eventType));
    }
  };
}

export function setAppointmentDetails(appointmentDetails) {
  return function (dispatch, getState) {
    if (!appointmentDetails) {
      dispatch(schedulerSlice.actions._setAppointmentDetails(null));
      return;
    }
    const eventType = appointmentDetails?.eventType;
    dispatch(setSelectedEventType(eventType));
    const didSet = getState().screens.scheduler.selectedEventType === eventType;
    if (didSet) {
      dispatch(schedulerSlice.actions._setAppointmentDetails(appointmentDetails));
    }
  };
}

const INITIAL_STATE = {
  status: PENDING,
  error: null,
  apptToCancel: null,
  apptCanceled: false,
  aptsSlotAvailabilityStatus: PENDING,
  aptsSlotAvailabilityData: {},
  aptsSlotAvailabilityError: null,
  aptsSlotAvailabilityActiveRequest: null,
  aptsRequestStatus: PENDING,
  aptsRequestSuccessMessage: null,
  aptsRequestError: null,
  aptsRequestActiveRequest: null,
  cancelledAptsRequestStatus: PENDING,
  cancelledAptsRequestSuccessMessage: null,
  cancelledAptsRequestError: null,
  cancelledAptsRequestActiveRequest: null,
  canceledAppts: [],
  scheduleDataStatus: PENDING,
  scheduleData: [],
  scheduleDataError: null,
  scheduleDataActiveRequest: null,
  saveScheduleDataStatus: PENDING,
  saveScheduleDataError: null,
  saveScheduleDataSuccessMessage: null,
  aptsSlotOverrideStatus: PENDING,
  aptsSlotOverrideError: null,
  aptsSlotOverrideData: [],
  aptsSlotOverrideActiveRequest: null,
  saveAptsSlotOverridesStatus: PENDING,
  saveAptsSlotOverridesError: null,
  deleteAptsSlotOverridesStatus: PENDING,
  deleteAptsSlotOverridesError: null,
  appointmentRequestsFetchStatus: PENDING,
  appointmentRequestsFetchError: null,
  appointmentRequests: null,
  exportStatus: PENDING,
  exportError: null,
  exportSuccessMessage: null,
  sendSMSStatus: PENDING,
  sendSMSError: null,
  cmrAppointmentServicesStatus: PENDING,
  cmrAppointmentServicesData: [],
  cmrAppointmentServicesError: null,
  cmrAppointmentServicesActiveRequest: null,
  updateCMRAppointmentServicesStatus: PENDING,
  updateCMRAppointmentServicesError: null,
  selectedEventType: null,
  appointmentDetails: null,
  noPermissionForSelectedEventType: false,
  lastMonthFetched: null,
};
const schedulerSlice = createSlice({
  name: 'scheduler',
  initialState: INITIAL_STATE,
  reducers: {
    _setNoPermissionForSelectedEventType: (state, action) => {
      state.noPermissionForSelectedEventType = action.payload;
    },
    _setSelectedEventType: (state, action) => {
      state.noPermissionForSelectedEventType = false;
      state.selectedEventType = action.payload;
    },
    _setAppointmentDetails: (state, action) => {
      state.appointmentDetails = action.payload;
    },
    _setLastMonthFetched: (state, action) => {
      state.lastMonthFetched = action.payload;
    },
    resetState: (state) => {
      return {
        ...INITIAL_STATE,
      };
    },
    resetEventTypeSpecificState: (state) => {
      return {
        ...state,
        scheduleData: [],
        aptsSlotOverrideData: [],
        aptsSlotAvailabilityData: {},
        appointmentRequests: null,
        canceledAppts: [],
      };
    },
    resetExportState: (state) => {
      return {
        ...state,
        exportStatus: PENDING,
        exportError: null,
        exportSuccessMessage: null,
      };
    },
    resetCMRAttachedServicesState: (state) => {
      return {
        ...state,
        cmrAppointmentServicesStatus: PENDING,
        cmrAppointmentServicesData: [],
        cmrAppointmentServicesError: null,
        cmrAppointmentServicesActiveRequest: null,
        updateCMRAppointmentServicesStatus: PENDING,
        updateCMRAppointmentServicesError: null,
      };
    },
    resetStatus: (state) => {
      return {
        ...state,
        status: PENDING,
        error: null,
        aptsRequestStatus: PENDING,
        aptsRequestSuccessMessage: null,
        aptsRequestError: null,
        saveScheduleDataStatus: PENDING,
        saveScheduleDataError: null,
        saveScheduleDataSuccessMessage: null,
        aptsSlotAvailabilityStatus: PENDING,
        aptsSlotAvailabilityError: null,
        aptsSlotOverrideStatus: PENDING,
        aptsSlotOverrideError: null,
        saveAptsSlotOverridesStatus: PENDING,
        saveAptsSlotOverridesError: null,
        deleteAptsSlotOverridesStatus: PENDING,
        deleteAptsSlotOverridesError: null,
        appointmentRequestsFetchStatus: PENDING,
        appointmentRequestsFetchError: null,
        exportStatus: PENDING,
        exportError: null,
        exportSuccessMessage: null,
        sendSMSStatus: PENDING,
        sendSMSError: null,
      };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(_getAppointmentRequests.pending, (state, action) => {
        state.appointmentRequestsFetchStatus = REQUESTING;
        state.appointmentRequestsFetchError = null;
        state.aptsRequestActiveRequest = {
          eventType: action.meta.arg.eventType,
          requestId: action.meta.requestId,
        };
      })
      .addCase(_getAppointmentRequests.fulfilled, (state, action) => {
        if (state.aptsRequestActiveRequest?.requestId !== action.meta.requestId) {
          return;
        }
        state.appointmentRequestsFetchStatus = SUCCESS;
        state.appointmentRequestsFetchError = null;
        state.appointmentRequests = action.payload.requests;
        state.aptsRequestActiveRequest = null;
      })
      .addCase(_getAppointmentRequests.rejected, (state, action) => {
        if (state.aptsRequestActiveRequest?.requestId !== action.meta.requestId) {
          return;
        }
        state.appointmentRequestsFetchStatus = FAILED;
        state.appointmentRequestsFetchError = action.payload;
        state.appointmentRequests = null;
        state.aptsRequestActiveRequest = null;
      })
      .addCase(deleteAppointmentSlotOverride.pending, (state) => {
        state.deleteAptsSlotOverridesStatus = REQUESTING;
        state.deleteAptsSlotOverridesError = null;
      })
      .addCase(deleteAppointmentSlotOverride.fulfilled, (state, action) => {
        state.deleteAptsSlotOverridesStatus = SUCCESS;
      })
      .addCase(deleteAppointmentSlotOverride.rejected, (state, action) => {
        state.deleteAptsSlotOverridesStatus = FAILED;
        state.deleteAptsSlotOverridesError = action.payload;
      })
      .addCase(saveAppointmentSlotOverrides.pending, (state) => {
        state.saveAptsSlotOverridesStatus = REQUESTING;
        state.saveAptsSlotOverridesError = null;
      })
      .addCase(saveAppointmentSlotOverrides.fulfilled, (state, action) => {
        state.saveAptsSlotOverridesStatus = SUCCESS;
      })
      .addCase(saveAppointmentSlotOverrides.rejected, (state, action) => {
        state.saveAptsSlotOverridesStatus = FAILED;
        state.saveAptsSlotOverridesError = action.payload;
      })
      .addCase(getAppointmentSlotOverrides.pending, (state, action) => {
        state.aptsSlotOverrideActiveRequest = {
          eventType: action.meta.arg.eventType,
          month: action.meta.arg.month,
          requestId: action.meta.requestId,
        };
        state.aptsSlotOverrideStatus = REQUESTING;
        state.aptsSlotOverrideError = null;
      })
      .addCase(getAppointmentSlotOverrides.fulfilled, (state, action) => {
        if (state.aptsSlotOverrideActiveRequest?.requestId !== action.meta.requestId) {
          return;
        }
        state.aptsSlotOverrideStatus = SUCCESS;
        state.aptsSlotOverrideData = action.payload || [];
        state.aptsSlotOverrideError = null;
        state.aptsSlotOverrideActiveRequest = null;
      })
      .addCase(getAppointmentSlotOverrides.rejected, (state, action) => {
        if (state.aptsSlotOverrideActiveRequest?.requestId !== action.meta.requestId) {
          return;
        }
        state.aptsSlotOverrideStatus = FAILED;
        state.aptsSlotOverrideError = action.payload;
        state.aptsSlotOverrideData = [];
        state.aptsSlotOverrideActiveRequest = null;
      })
      .addCase(getAppointmentSlotAvailability.pending, (state, action) => {
        state.aptsSlotAvailabilityActiveRequest = {
          eventType: action.meta.arg.eventType,
          month: action.meta.arg.month,
          requestId: action.meta.requestId,
        };
        state.aptsSlotAvailabilityStatus = REQUESTING;
        state.aptsSlotAvailabilityError = null;
      })
      .addCase(getAppointmentSlotAvailability.fulfilled, (state, action) => {
        if (state.aptsSlotAvailabilityActiveRequest?.requestId !== action.meta.requestId) {
          return;
        }
        state.aptsSlotAvailabilityStatus = SUCCESS;
        state.aptsSlotAvailabilityError = null;
        state.aptsSlotAvailabilityData = action.payload || {};
        state.aptsSlotAvailabilityActiveRequest = null;
      })
      .addCase(getAppointmentSlotAvailability.rejected, (state, action) => {
        if (state.aptsSlotAvailabilityActiveRequest?.requestId !== action.meta.requestId) {
          return;
        }
        state.aptsSlotAvailabilityStatus = FAILED;
        state.aptsSlotAvailabilityError = action.payload;
        state.aptsSlotAvailabilityData = {};
        state.aptsSlotAvailabilityActiveRequest = null;
      })
      .addCase(saveAppointmentsSchedule.pending, (state) => {
        state.saveScheduleDataStatus = REQUESTING;
        state.saveScheduleDataError = null;
        state.saveScheduleDataSuccessMessage = null;
      })
      .addCase(saveAppointmentsSchedule.fulfilled, (state, action) => {
        state.saveScheduleDataStatus = SUCCESS;
        state.saveScheduleDataSuccessMessage = 'Success!';
      })
      .addCase(saveAppointmentsSchedule.rejected, (state, action) => {
        state.saveScheduleDataStatus = FAILED;
        state.saveScheduleDataError = action.payload;
      })
      .addCase(getAppointmentsScheduleAuthed.pending, (state, action) => {
        state.scheduleDataActiveRequest = {
          eventType: action.meta.arg.eventType,
          requestId: action.meta.requestId,
        };
        state.scheduleDataStatus = REQUESTING;
        state.scheduleDataError = null;
      })
      .addCase(getAppointmentsScheduleAuthed.fulfilled, (state, action) => {
        if (state.scheduleDataActiveRequest?.requestId !== action.meta.requestId) {
          return;
        }
        state.scheduleDataActiveRequest = null;
        state.scheduleDataStatus = SUCCESS;
        state.scheduleDataError = null;
        state.scheduleData = action.payload?.filter?.((s) => !!s.employerSchedulerHoursId) || [];
      })
      .addCase(getAppointmentsScheduleAuthed.rejected, (state, action) => {
        if (state.scheduleDataActiveRequest?.requestId !== action.meta.requestId) {
          return;
        }
        state.scheduleDataActiveRequest = null;
        state.scheduleDataStatus = FAILED;
        state.scheduleDataError = action.payload;
        state.scheduleData = [];
      })
      .addCase(submitPHARequest.pending, (state) => {
        state.aptsRequestStatus = REQUESTING;
        state.aptsRequestError = null;
        state.aptsRequestSuccessMessage = null;
      })
      .addCase(submitPHARequest.fulfilled, (state, action) => {
        state.aptsRequestStatus = SUCCESS;
        state.aptsRequestSuccessMessage = action.payload.data?.message;
      })
      .addCase(submitPHARequest.rejected, (state, action) => {
        state.aptsRequestStatus = FAILED;
        state.aptsRequestError = action.payload;
        state.aptsRequestSuccessMessage = null;
      })
      .addCase(exportAppointmentRequests.pending, (state) => {
        state.exportStatus = REQUESTING;
        state.exportError = null;
        state.exportSuccessMessage = null;
      })
      .addCase(exportAppointmentRequests.fulfilled, (state, action) => {
        state.exportStatus = SUCCESS;
        state.exportSuccessMessage = 'Export downloaded successfully';
      })
      .addCase(exportAppointmentRequests.rejected, (state, action) => {
        state.exportStatus = FAILED;
        state.exportError = action.payload;
        state.exportSuccessMessage = null;
      })
      .addCase(cancelAppointment.pending, (state) => {
        state.status = REQUESTING;
        state.error = null;
      })
      .addCase(cancelAppointment.fulfilled, (state, action) => {
        state.status = SUCCESS;
        state.apptCanceled = true;
      })
      .addCase(cancelAppointment.rejected, (state, action) => {
        state.status = FAILED;
        state.error = action.payload;
      })
      .addCase(fetchAppointmentByCancelToken.pending, (state) => {
        state.status = REQUESTING;
        state.error = null;
      })
      .addCase(fetchAppointmentByCancelToken.fulfilled, (state, action) => {
        state.status = SUCCESS;
        state.apptToCancel = action.payload;
      })
      .addCase(fetchAppointmentByCancelToken.rejected, (state, action) => {
        state.status = FAILED;
        state.error = action.payload;
      })
      .addCase(_fetchCanceledAppointments.pending, (state, action) => {
        state.cancelledAptsRequestActiveRequest = {
          eventType: state.selectedEventType,
          requestId: action.meta.requestId,
        };
        state.cancelledAptsRequestStatus = REQUESTING;
        state.cancelledAptsRequestError = null;
      })
      .addCase(_fetchCanceledAppointments.fulfilled, (state, action) => {
        if (state.cancelledAptsRequestActiveRequest?.requestId !== action.meta.requestId) {
          return;
        }
        state.cancelledAptsRequestActiveRequest = null;
        state.cancelledAptsRequestStatus = SUCCESS;
        state.cancelledAptsRequestError = null;
        state.canceledAppts = action.payload;
      })
      .addCase(_fetchCanceledAppointments.rejected, (state, action) => {
        if (state.cancelledAptsRequestActiveRequest?.requestId !== action.meta.requestId) {
          return;
        }
        state.cancelledAptsRequestActiveRequest = null;
        state.cancelledAptsRequestStatus = FAILED;
        state.cancelledAptsRequestError = action.payload;
      })
      .addCase(sendAppointmentRequestSms.pending, (state) => {
        state.sendSMSStatus = REQUESTING;
        state.sendSMSError = null;
      })
      .addCase(sendAppointmentRequestSms.fulfilled, (state, action) => {
        state.sendSMSStatus = SUCCESS;
      })
      .addCase(sendAppointmentRequestSms.rejected, (state, action) => {
        state.sendSMSStatus = FAILED;
        state.sendSMSError = action.payload;
      })
      .addCase(getCMRAppointmentServices.pending, (state, action) => {
        state.cmrAppointmentServicesActiveRequest = {
          eventId: action.meta.arg.eventId,
          requestId: action.meta.requestId,
        };
        state.cmrAppointmentServicesStatus = REQUESTING;
        state.cmrAppointmentServicesError = null;
      })
      .addCase(getCMRAppointmentServices.fulfilled, (state, action) => {
        if (state.cmrAppointmentServicesActiveRequest?.requestId !== action.meta.requestId) {
          return;
        }
        state.cmrAppointmentServicesActiveRequest = null;
        state.cmrAppointmentServicesStatus = SUCCESS;
        state.cmrAppointmentServicesData = action.payload || [];
      })
      .addCase(getCMRAppointmentServices.rejected, (state, action) => {
        if (state.cmrAppointmentServicesActiveRequest?.requestId !== action.meta.requestId) {
          return;
        }
        state.cmrAppointmentServicesActiveRequest = null;
        state.cmrAppointmentServicesStatus = FAILED;
        state.cmrAppointmentServicesData = [];
        state.cmrAppointmentServicesError = action.payload;
      })
      .addCase(updateCMRAppointmentServices.pending, (state) => {
        state.updateCMRAppointmentServicesStatus = REQUESTING;
        state.updateCMRAppointmentServicesError = null;
      })
      .addCase(updateCMRAppointmentServices.fulfilled, (state) => {
        state.updateCMRAppointmentServicesStatus = SUCCESS;
      })
      .addCase(updateCMRAppointmentServices.rejected, (state, action) => {
        state.updateCMRAppointmentServicesStatus = FAILED;
        state.updateCMRAppointmentServicesError = action.payload;
      });
  },
});

export const {
  resetState,
  resetStatus,
  resetEventTypeSpecificState,
  resetExportState,
  resetCMRAttachedServicesState,
} = schedulerSlice.actions;
export default schedulerSlice.reducer;
